import { ReactElement, useCallback, useMemo } from 'react';
import {
  Typography,
  DialogContent,
  Stack,
  DialogContentText,
  DialogActions,
  Avatar,
  createFilterOptions,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { useFormik, FormikHelpers } from 'formik';
import { useSnackbar } from 'notistack';

import { hasError, getHelperText } from '~utils/formHelpers';
import { useLazyRequest } from '~hooks/useLazyRequest';
import { assignedUserToItems } from '~services/item';
import { getDefaultAvatar } from '~utils/user';
import { CustomAutocomplete, UserInfo } from '~components/index';
import { Roles } from '~globals/types/enums';

import { DialogModifyAssignmentOrdersContentProps } from './types';
import { FIELDS_NAME, initialValues, validationSchema } from './utils';
import {
  DialogModifyAssignmentOrdersTitle,
  DialogModifyAssignmentOrdersTitleCloseIcon,
  DialogModifyAssignmentOrdersAlert,
} from './styles';

const DialogModifyAssignmentContentOrders = ({
  onClose,
  itemIds,
  onSuccessModify,
  users,
}: DialogModifyAssignmentOrdersContentProps): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();

  const [, loadingAssignedUserToItems, , executeAssignedUserToItems] =
    useLazyRequest({
      request: assignedUserToItems,
      withPostSuccess: () => {
        enqueueSnackbar(
          'Los pedidos seleccionados fueron asignados al usuario o chofer seleccionado.',
          { variant: 'success' },
        );

        onSuccessModify?.();

        onClose();
      },
      withPostFailure: () => {
        enqueueSnackbar(
          'No se pudo realizar la asignación, intente nuevamente',
          { variant: 'error' },
        );
      },
    });

  const onSubmit = useCallback(
    async (
      values: typeof initialValues,
      { setSubmitting }: FormikHelpers<typeof initialValues>,
    ) => {
      await executeAssignedUserToItems({
        itemIds,
        assignedUserId: values[FIELDS_NAME.ASSIGNED_USER],
      });

      setSubmitting(false);
    },
    [itemIds, executeAssignedUserToItems],
  );

  const {
    submitForm,
    isSubmitting,
    touched,
    errors,
    setFieldValue,
    setFieldTouched,
    dirty,
    isValid,
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });

  const disabledSubmit = useMemo(() => !dirty || !isValid, [dirty, isValid]);

  return (
    <>
      <DialogModifyAssignmentOrdersTitle>
        <Typography component="h2" variant="h6">
          Modificar asignación de pedidos
        </Typography>

        <DialogModifyAssignmentOrdersTitleCloseIcon
          aria-label="close"
          onClick={onClose}
          size="small"
        >
          <CloseIcon />
        </DialogModifyAssignmentOrdersTitleCloseIcon>
      </DialogModifyAssignmentOrdersTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <DialogContentText color="text.secondary" variant="caption">
            Al confirmar esta acción, los pedidos seleccionados serán asignados
            al usuario o chofer seleccionado. El cambio quedará registrado en el
            historial del pedido.
          </DialogContentText>

          <Stack spacing={1}>
            <DialogModifyAssignmentOrdersAlert
              severity="info"
              variant="standard"
              icon={false}
            >
              Si un pedido se encuentra actualmente relacionado a una ruta en
              progreso, no se modificará su asignación. Para modificar la
              asignación de pedidos en curso, utilice la app del chofer.
            </DialogModifyAssignmentOrdersAlert>

            <CustomAutocomplete
              options={users}
              label="Asignar usuario"
              name={FIELDS_NAME.ASSIGNED_USER}
              onChange={(_e, val) => {
                setFieldValue(FIELDS_NAME.ASSIGNED_USER, val?.userId ?? '');
              }}
              onBlur={() => {
                setFieldTouched(FIELDS_NAME.ASSIGNED_USER, true);
              }}
              error={hasError(touched, errors, FIELDS_NAME.ASSIGNED_USER)}
              helperText={getHelperText(
                touched,
                errors,
                FIELDS_NAME.ASSIGNED_USER,
              )}
              getOptionLabel={(option) =>
                `${option.lastName}, ${option.firstName}`
              }
              renderOption={(props, option) => (
                <li
                  {...props}
                  key={option.userId}
                  style={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <UserInfo
                    name={`${option.lastName}, ${option.firstName}`}
                    urlAvatar={option.avatarUrl || undefined}
                  />
                </li>
              )}
              startAdornment={(val) =>
                val && (
                  <Avatar
                    src={getDefaultAvatar(val.avatarUrl || undefined)}
                    alt={`${val.lastName}, ${val.firstName}`}
                    sx={{ width: 20, height: 20 }}
                  />
                )
              }
              groupBy={(option) => {
                if (option.roleId === Roles.Drive) {
                  return 'Choferes';
                }

                return 'Usuarios';
              }}
              filterOptions={createFilterOptions({
                ignoreAccents: true,
                ignoreCase: true,
                stringify: (option) =>
                  `${option.lastName}, ${option.firstName}`.replace(',', ''),
              })}
            />
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions>
        <LoadingButton
          variant="contained"
          color="primary"
          onClick={submitForm}
          loading={loadingAssignedUserToItems || isSubmitting}
          disabled={disabledSubmit}
        >
          Confirmar
        </LoadingButton>
      </DialogActions>
    </>
  );
};

export default DialogModifyAssignmentContentOrders;
